import React from 'react';

import { InternalLink } from '../../../components/Clickables/InternalLink/InternalLink';
import { Heading } from '../../../components/Heading/Heading';

interface IProps {
  appView?: boolean;
}

const PrivacyPolicy = ({ appView = true }: IProps) => {
  return (
    <div className="text-justify leading-6">
      <Heading as="h1" className="mb-4">
        Regional consumer privacy notice for U.S. residents
      </Heading>
      <p>
        <span>
          This regional Regional privacy notice applies for applicable US residents only and applies{' '}
        </span>
        <span>
          <strong>in addition</strong>
        </span>
        <span>&nbsp;to Fishbrains main Privacy </span>
        <span>Policy which can be found </span>
        {appView ? (
          <InternalLink to="/policies/privacy/latest">here</InternalLink>
        ) : (
          <InternalLink to="/privacy">here</InternalLink>
        )}
      </p>
      <p>
        <span>
          In this notice, we explain how we collect, use, and disclose your personal information. We
          also explain how you exercise your rights under the following privacy regulations which we
          collectively refer to as &quot;U.S. privacy laws.&quot;
        </span>
      </p>
      <p>
        <span className="pl-6">California Consumer Privacy Act, Colorado Privacy Act,</span>
        <br />
        <span className="pl-6">
          Connecticut Act Concerning Personal Data Privacy and Online Monitoring,
        </span>
        <br />
        <span className="pl-6">Virginia Consumer Data Protection Act.</span>
        <br />
        <span className="pl-6">Utah Consumer Privacy Act, and</span>
      </p>
      <p>
        <span>
          In this message, &quot;personal information&quot; refers to information that identifies,
          relates to, describes, is reasonably capable of being associated with, or could reasonably
          be linked, directly or indirectly, to you. Personal information does not include
          information that cannot reasonably be linked to you. When data is deidentified (as defined
          in the U.S. privacy laws), we handle it in de-identified form and do not attempt to make
          the information usable to identify any individual again.
          <br />
          In accordance with the limitations described in our Privacy policies, we may disclose your
          personal information for business purposes, with strict restrictions on how our partners
          may use and disclose the data we provide. We may also disclose your data on your behalf or
          in other ways in accordance with U.S. privacy laws. We never sell your personal
          information or &quot;share&quot; your personal information, as defined in the California
          Consumer Privacy Act (CCPA).
        </span>
      </p>
      <p>
        <span>
          The information we collect, use, and disclose about you may vary depending on how you
          interact with our products. This notice, below is an overview of:
        </span>
      </p>
      <ul className="list-disc pl-4">
        <li className="text-sm">
          <span>
            The categories of personal information we may have collected about you in the past 12
            months.
          </span>
        </li>
        <li className="text-sm">
          <span>How we may use your personal information.</span>
        </li>
        <li className="text-sm">
          <span>To whom we may have disclosed the information.</span>
          <span>
            <br />
          </span>
        </li>
      </ul>
      <Heading color="pinktail" variant="sm" as="h2" className="mb-4">
        App users, customers, prospective customers, corporate representatives of our vendors and
        other partners
      </Heading>
      <p>
        <span>
          <strong>What Categories of Personal Information Do We Collect?</strong>
          <br />
          <br />
        </span>
        <span>
          Fishbrain&#39;s Services are for logging, tracking, and sharing fishing-related
          information, buying and rating fishing-related products through the Fishbrain Shop, and/or
          such other Services as we may make available from time to time. To provide these Services
          we collect, use, and store information about you.
        </span>
      </p>
      <p>
        <strong>Non-Sensitive Personal Information we collect:</strong>
      </p>
      <ul className="list-disc pl-4">
        <li className="text-sm">
          <strong>Identifiers</strong>
          <span>
            . <br />
            Examples: A real name, alias, postal address, unique personal identifier, online
            identifier, Internet Protocol address, email address, account name, ID number, or other
            similar identifiers.
          </span>
        </li>
        <li className="text-sm">
          <strong>
            Personal information categories listed in the California Customer Records statute (Cal.
            Civ. Code &sect; 1798.80(e)). <br />
          </strong>
          <span>
            Examples: A name, signature, ID number, physical characteristics or description,
            address, address, address, address, telephone number, state identification card number,
            bank number, credit card number, debit card number, or any other financial information,.
            Some included in this category may overlap with other categories.
          </span>
        </li>
        <li className="text-sm">
          <strong>
            Commercial information. <br />
          </strong>
          <span>
            Examples: Products or services purchased, obtained, or considered, or other purchasing
            or consuming histories or tendencies.
          </span>
        </li>
        <li className="text-sm">
          <strong>
            Internet or other similar network activity. <br />
          </strong>
          <span>
            Examples: Browsing history, search history, information on a consumer&#39;s interaction
            with a website, application, or advertisement.
          </span>
        </li>
        <li className="text-sm">
          <strong>
            Geolocation data. <br />
          </strong>
          <span>Examples: Physical locations or movements.</span>
        </li>
        <li className="text-sm">
          <strong>
            Sensory data. <br />
          </strong>
          <span>
            Examples: Audio, electronic, visual, thermal, olfactory, or similar information.
          </span>
        </li>
        <li className="text-sm">
          <strong>Inferences drawn from other personal information</strong>
          <span>
            . <br />
            Examples: Profile reflecting a person&#39;s preferences, characteristics, psychological
            trends, predispositions, behavior, attitudes, intelligence, abilities, and aptitudes.
          </span>
        </li>
      </ul>
      <Heading variant="sm" as="h2" className="mb-4">
        For What Purposes Do We Collect and Use Personal Information?
      </Heading>
      <p>
        <span>
          We use personal information about you as a customer, prospective customer, corporate
          representative of our vendors and other partners for the purposes of:
        </span>
      </p>
      <ul className="list-disc pl-4">
        <li className="text-sm">
          <span>
            To prevent, detect, and investigate security incidents that compromise the availability,
            authenticity, integrity, or confidentiality of stored or transmitted personal
            information, including in or via our premises, computers, software, networks,
            communications devices, and other similar system;
          </span>
        </li>
        <li className="text-sm">
          <span>
            To resist malicious, deceptive, fraudulent or illegal actions directed at us or our
            users and to prosecute those responsible for those actions;
          </span>
        </li>
        <li className="text-sm">
          <span>To ensure the physical safety of natural persons;</span>
        </li>
        <li className="text-sm">
          <span>For short-term, transient use;</span>
        </li>
        <li className="text-sm">
          <span>To perform services on behalf of us;</span>
        </li>
        <li className="text-sm">
          <span>To verify or maintain the quality or safety of our services and products;</span>
        </li>
        <li className="text-sm">
          <span>To improve, upgrade, or enhance our services and products; </span>
        </li>
        <li className="text-sm">
          <span>To market to you; and</span>
        </li>
        <li className="text-sm">
          <span>
            To collect or process it where such collection or processing is not for the purpose of
            inferring characteristics about a consumer such as to perform functions that are
            required under laws that apply to us and to support any claim or defense that we could
            face before any jurisdictional and/or administrative authority, arbitration, or
            mediation panel, and cooperating with &ndash; or informing &ndash; law enforcement or
            regulatory authorities to the extent required by law.
          </span>
        </li>
      </ul>
      <Heading variant="sm" as="h2" className="mb-4">
        What Criteria Do We Consider When Retaining Personal Information?
      </Heading>
      <p>
        <span>
          In general, with respect to categories of personal information about customers,
          prospective customers, corporate representatives of our vendors and other partners, we
          retain each category for the longer of: until we determine that the Company will not be
          engaged by or engage the customer, vendor, or partner, or if a customer, vendor or partner
          engages or is engaged by the Company, until the end of such engagement, plus any
          additional time periods necessary for the compliance with laws, exercise or defense of
          legal rights, and archiving, back-up and deletion processes.
        </span>
      </p>
    </div>
  );
};

// Needs default to auto load correct file

export default PrivacyPolicy;
